


import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaSync, FaSpinner } from 'react-icons/fa';
import alaram from "../../../../context/alaram.mp3";
import adminWebSocketService from '../../../../context/AdminWebSocketService';
import { getISTDateTime12hr } from '../../../../utils/dateTimeUtils';
import { useNotification } from '../../../../context/Notifications/NotificationContext';


// ============================================
// STYLES
// ============================================
const styles = {
    container: {
        padding: '24px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'white',
        padding: '16px 24px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    title: {
        margin: 0,
        color: '#e66d33',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    refreshButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        background: '#e66d33',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: '0.2s',
        fontSize: '14px'
    },
    tableContainer: {
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px'
    },
    th: {
        background: '#f1f5f9',
        color: '#475569',
        fontWeight: '600',
        padding: '16px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0'
    },
    td: {
        padding: '16px',
        borderBottom: '1px solid #e2e8f0',
        color: '#334155'
    },
    statusBadge: {
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block'
    },
    viewButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 14px',
        background: '#2563eb',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '500',
        transition: '0.2s'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#94a3b8'
    },
    loadingOverlay: {
        textAlign: 'center',
        padding: '40px 20px',
        color: '#64748b'
    },
    notificationBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        background: '#dc2626',
        color: 'white',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        marginLeft: '8px',
        animation: 'pulse 1.5s ease-in-out infinite'
    },
    newOrderRow: {
        animation: 'highlightNew 2s ease-in-out'
    }
};

// ============================================
// COMPONENT
// ============================================
const DeliveryLiveOrders = () => {
    const navigate = useNavigate();
    const { addNotification } = useNotification(); // Get the notification function
    const ALLOWED_APP_TYPES = ['FOOD_AND_BEVERAGES'];
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [newOrderCount, setNewOrderCount] = useState(0);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [hasNewOrders, setHasNewOrders] = useState(false);
    const [newOrderIds, setNewOrderIds] = useState(new Set());
    const [wsMessageCount, setWsMessageCount] = useState(0);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
    const [rawApiData, setRawApiData] = useState(null);
    const [rawWsData, setRawWsData] = useState(null);

    // Audio refs
    const audioRef = useRef(null);
    const audioPlayedRef = useRef(false);

    // ─── Audio Setup ──────────────────────────────────────────────
    useEffect(() => {
        audioRef.current = new Audio(alaram);
        audioRef.current.load();

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // ─── Play Notification Sound ──────────────────────────────────
    const playNotificationSound = async () => {
        if (!audioRef.current || isAudioPlaying) return;

        try {
            setIsAudioPlaying(true);
            audioRef.current.currentTime = 0;
            await audioRef.current.play();

            audioRef.current.onended = () => {
                setIsAudioPlaying(false);
                audioPlayedRef.current = false;
            };
        } catch (error) {
            console.warn('Audio playback failed:', error);
            setIsAudioPlaying(false);
            audioPlayedRef.current = false;
        }
    };

    // ─── Show Browser Notification ──────────────────────────────
    const showBrowserNotification = (count) => {
        if (!("Notification" in window)) return;

        if (Notification.permission === "granted") {
            new Notification(`🆕 ${count} New Order${count > 1 ? 's' : ''}!`, {
                body: `Click to view ${count} new order${count > 1 ? 's' : ''} assigned to riders.`,
                icon: "🛵",
                silent: true
            });
        } else if (Notification.permission === "default") {
            Notification.requestPermission();
        }
    };

    // ─── Request Notification Permission ─────────────────────────
    useEffect(() => {
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []);

    // ─── Process New Order from WebSocket ────────────────────────
    // ─── Process New Order from WebSocket ────────────────────────
    // ─── Process New Order from WebSocket ────────────────────────
    // const handleNewOrder = (orderData) => {
    //     console.log('═══════════════════════════════════════════════');
    //     console.log('🆕🆕🆕 NEW ORDER FROM WEBSOCKET 🆕🆕🆕');
    //     console.log('═══════════════════════════════════════════════');
    //     console.log('📋 Order ID:', orderData.id || orderData.orderId);
    //     console.log('📋 Partner ID:', orderData.partnerId);
    //     console.log('📋 Status:', orderData.status || 'N/A');

    //     const orderId = orderData.id || orderData.orderId;

    //     // ✅ Use functional update to avoid stale closure
    //     setOrders(prevOrders => {
    //         // Check if order exists in current state
    //         const existingOrderIndex = prevOrders.findIndex(order =>
    //             (order.id === orderId || order.orderId === orderId)
    //         );

    //         if (existingOrderIndex !== -1) {
    //             // ✅ REPLACE: Remove old and add new at top
    //             console.log(`🔄 Order ${orderId} already exists, REPLACING...`);
    //             console.log(`🔄 Old Partner ID: ${prevOrders[existingOrderIndex].partnerId}`);
    //             console.log(`🔄 New Partner ID: ${orderData.partnerId}`);

    //             const updatedOrders = [...prevOrders];
    //             updatedOrders.splice(existingOrderIndex, 1); // Remove old
    //             return [orderData, ...updatedOrders]; // Add new at top
    //         }

    //         // ✅ New order - add to top
    //         console.log(`✅ Adding NEW order ${orderId} to the list`);
    //         return [orderData, ...prevOrders];
    //     });

    //     // Mark as NEW for highlighting
    //     setNewOrderIds(prev => new Set(prev).add(orderId));
    //     setNewOrderCount(prev => prev + 1);
    //     setHasNewOrders(true);

    //     // Play sound
    //     if (!audioPlayedRef.current) {
    //         audioPlayedRef.current = true;
    //         playNotificationSound();
    //         setTimeout(() => {
    //             audioPlayedRef.current = false;
    //         }, 3000);
    //     }

    //     // Show browser notification
    //     showBrowserNotification(1);

    //     // Remove highlight after 5 seconds
    //     setTimeout(() => {
    //         setNewOrderIds(prev => {
    //             const newSet = new Set(prev);
    //             newSet.delete(orderId);
    //             return newSet;
    //         });
    //     }, 5000);

    //     console.log('═══════════════════════════════════════════════');
    // };
    // const handleNewOrder = (orderData) => {
    //     console.log('🆕 NEW ORDER FROM WEBSOCKET:', orderData);

    //     const orderId = orderData.id || orderData.orderId;

    //     setOrders(prevOrders => {
    //         const existingOrderIndex = prevOrders.findIndex(order =>
    //             (order.id === orderId || order.orderId === orderId)
    //         );

    //         if (existingOrderIndex !== -1) {
    //             console.log(`🔄 Order ${orderId} already exists, REPLACING...`);
    //             const updatedOrders = [...prevOrders];
    //             updatedOrders.splice(existingOrderIndex, 1);
    //             return [orderData, ...updatedOrders];
    //         }

    //         console.log(`✅ Adding NEW order ${orderId} to the list`);
    //         return [orderData, ...prevOrders];
    //     });

    //     setNewOrderIds(prev => new Set(prev).add(orderId));
    //     setNewOrderCount(prev => prev + 1);
    //     setHasNewOrders(true);

    //     // 🔔 ADD GLOBAL NOTIFICATION (this will play sound and show badge)
    //     addNotification('Delivery orders', 1);

    //     // Remove highlight after 5 seconds
    //     setTimeout(() => {
    //         setNewOrderIds(prev => {
    //             const newSet = new Set(prev);
    //             newSet.delete(orderId);
    //             return newSet;
    //         });
    //     }, 5000);
    // };

    const handleNewOrder = (orderData) => {
        // console.log('🆕 NEW ORDER FROM WEBSOCKET:', orderData);

        const orderId = orderData.id || orderData.orderId;

        // ✅ CHECK appType FIRST - ONLY FOOD_AND_BEVERAGES
        const appType = orderData.appType?.toUpperCase() || '';
        if (appType !== 'FOOD_AND_BEVERAGES') {
            // console.log(`⏭️ Order ${orderId} appType "${appType}" is not FOOD_AND_BEVERAGES, skipping`);
            return;
        }

        setOrders(prevOrders => {
            const existingOrderIndex = prevOrders.findIndex(order =>
                (order.id === orderId || order.orderId === orderId)
            );

            if (existingOrderIndex !== -1) {
                // console.log(`🔄 Order ${orderId} already exists, REPLACING...`);
                const updatedOrders = [...prevOrders];
                updatedOrders.splice(existingOrderIndex, 1);
                return [orderData, ...updatedOrders];
            }

            // console.log(`✅ Adding NEW FOOD_AND_BEVERAGES order ${orderId} to the list`);
            return [orderData, ...prevOrders];
        });

        setNewOrderIds(prev => new Set(prev).add(orderId));
        setNewOrderCount(prev => prev + 1);
        setHasNewOrders(true);

        // 🔔 ADD GLOBAL NOTIFICATION (only for FOOD_AND_BEVERAGES)
        addNotification('Delivery orders', 1);

        // Remove highlight after 5 seconds
        setTimeout(() => {
            setNewOrderIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(orderId);
                return newSet;
            });
        }, 5000);
    };
    // ─── Initial Fetch (Only once on load) ──────────────────────
    // const fetchInitialOrders = async () => {
    //     console.log('═══════════════════════════════════════════════');
    //     console.log('🔄🔄🔄 FETCHING INITIAL ORDERS FROM API 🔄🔄🔄');
    //     console.log('═══════════════════════════════════════════════');

    //     try {
    //         setError(null);
    //         const token = localStorage.getItem("authToken");
    //         console.log(`🔑 Auth token present: ${!!token}`);
    //         if (token) {
    //             console.log(`🔑 Token preview: ${token.substring(0, 20)}...`);
    //         }

    //         console.log('📤 Making API request...');
    //         const response = await fetch("https://backend.maamaas.com/delivery/api/admin/assigned-orders", {
    //             method: 'GET',
    //             headers: {
    //                 'Authorization': `Bearer ${token}`,
    //                 'Content-Type': 'application/json'
    //             }
    //         });

    //         console.log(`📥 Response status: ${response.status} ${response.statusText}`);
    //         console.log(`📥 Response OK: ${response.ok}`);

    //         if (!response.ok) {
    //             throw new Error(`Failed to fetch orders: ${response.status}`);
    //         }

    //         const data = await response.json();
    //         console.log(`✅ API fetch successful!`);
    //         console.log(`📊 Total orders from API: ${data.length}`);
    //         setRawApiData(data);

    //         // Log first 3 orders for inspection
    //         if (data.length > 0) {
    //             console.log('📋 Sample order (first):', JSON.stringify(data[0], null, 2));
    //             console.log('📋 Order fields:', Object.keys(data[0]));
    //             console.log('📋 First 3 orders:', data.slice(0, 3).map(o => ({
    //                 orderId: o.orderId,
    //                 createdAt: o.createdAt,
    //                 userName: o.userName,
    //                 status: o.status
    //             })));
    //         } else {
    //             console.warn('⚠️ API returned empty array');
    //         }

    //         const orderArray = Array.isArray(data) ? data : [];
    //         console.log(`📊 Setting orders state with ${orderArray.length} orders`);
    //         setOrders(orderArray);
    //         setIsInitialLoadComplete(true);

    //     } catch (err) {
    //         console.error('❌❌❌ API ERROR ❌❌❌');
    //         console.error('Error message:', err.message);
    //         console.error('Error stack:', err.stack);
    //         setError(err.message || "Could not load live orders.");
    //         setOrders([]);
    //         setIsInitialLoadComplete(true);
    //     } finally {
    //         setLoading(false);
    //         setRefreshing(false);
    //         console.log('🔄 API fetch completed');
    //         console.log('═══════════════════════════════════════════════');
    //     }
    // };
    const fetchInitialOrders = async () => {
        // console.log('═══════════════════════════════════════════════');
        // console.log('🔄🔄🔄 FETCHING INITIAL ORDERS FROM API 🔄🔄🔄');
        // console.log('═══════════════════════════════════════════════');

        try {
            setError(null);
            const token = localStorage.getItem("authToken");


            // console.log('📤 Making API request...');
            const response = await fetch("https://backend.maamaas.com/delivery/api/admin/assigned-orders", {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });



            if (!response.ok) {
                throw new Error(`Failed to fetch orders: ${response.status}`);
            }

            const data = await response.json();
            // console.log(`✅ API fetch successful!`);
            // console.log(`📊 Total orders from API: ${data.length}`);
            setRawApiData(data);

            // if (data.length > 0) {
            //     console.log('📋 Sample order (first):', JSON.stringify(data[0], null, 2));
            //     console.log('📋 Order fields:', Object.keys(data[0]));
            // } else {
            //     console.warn('⚠️ API returned empty array');
            // }

            const orderArray = Array.isArray(data) ? data : [];

            // ✅ FILTER BY appType - ONLY FOOD_AND_BEVERAGES
            const filteredOrders = orderArray.filter(order => {
                const appType = order.appType?.toUpperCase() || '';
                return appType === 'FOOD_AND_BEVERAGES';
            });

            // console.log(`✅ Filtered to ${filteredOrders.length} orders with appType: FOOD_AND_BEVERAGES`);
            // console.log('📋 First 3 filtered orders:', filteredOrders.slice(0, 3).map(o => ({
            //     orderId: o.orderId,
            //     createdAt: o.createdAt,
            //     userName: o.userName,
            //     status: o.status,
            //     appType: o.appType
            // })));

            setOrders(filteredOrders);
            setIsInitialLoadComplete(true);

        } catch (err) {
            console.error('❌❌❌ API ERROR ❌❌❌');
            console.error('Error message:', err.message);
            console.error('Error stack:', err.stack);
            setError(err.message || "Could not load live orders.");
            setOrders([]);
            setIsInitialLoadComplete(true);
        } finally {
            setLoading(false);
            setRefreshing(false);
            // console.log('🔄 API fetch completed');
            // console.log('═══════════════════════════════════════════════');
        }
    };
    // ─── Log orders state changes ──────────────────────────────────
    // useEffect(() => {
    //     // console.log('📊📊📊 ORDERS STATE UPDATED 📊📊📊');
    //     // console.log(`📊 Total orders in state: ${orders.length}`);
    //     if (orders.length > 0) {
    //         console.log('📋 Order IDs in state:', orders.map(o => o.orderId || o.id));
    //         console.log('📋 Latest order:', orders[0] ? {
    //             orderId: orders[0].orderId,
    //             createdAt: orders[0].createdAt,
    //             userName: orders[0].userName,
    //             status: orders[0].status
    //         } : 'No orders');
    //     } else {
    //         console.log('📋 Orders array is empty');
    //     }
    // }, [orders]);

    // ─── WebSocket Setup ──────────────────────────────────────────
    useEffect(() => {
        // console.log('═══════════════════════════════════════════════');
        // console.log('🚀🚀🚀 DeliveryLiveOrders MOUNTED 🚀🚀🚀');
        // console.log('═══════════════════════════════════════════════');

        // Check WebSocket connection status
        const wsStatus = adminWebSocketService.isConnected();
        // console.log(`🔗 WebSocket connection status: ${wsStatus ? '✅ Connected' : '❌ Not connected'}`);

        // Log WebSocket service details
        // console.log('🔍 WebSocket Service Details:', {
        //     isConnected: adminWebSocketService.isConnected(),
        //     adminId: adminWebSocketService.adminId,
        //     subscriptionCount: adminWebSocketService.subscriptions?.size || 0,
        // });

        // Fetch initial orders
        fetchInitialOrders();

        // ─── WebSocket Message Listener ───────────────────────────
        // const handleWebSocketMessage = (event) => {
        //     console.log('═══════════════════════════════════════════════');
        //     console.log('📨📨📨 WEBSOCKET MESSAGE RECEIVED! 📨📨📨');
        //     console.log('═══════════════════════════════════════════════');
        //     console.log('⏰ Received at:', new Date().toISOString());

        //     setWsMessageCount(prev => {
        //         const newCount = prev + 1;
        //         console.log(`📊 WebSocket message count: ${newCount}`);
        //         return newCount;
        //     });

        //     console.log('📋 Topic:', event.detail.topic);
        //     console.log('📋 Topic Path:', event.detail.topicPath);
        //     console.log('📦 Data type:', typeof event.detail.data);
        //     console.log('📦 Raw Data:', event.detail.data);
        //     setRawWsData(event.detail.data);

        //     // Check if data is an array or object
        //     if (Array.isArray(event.detail.data)) {
        //         console.log(`📦 Data is an array with ${event.detail.data.length} items`);
        //         console.log('📋 First item:', event.detail.data[0]);
        //     } else if (typeof event.detail.data === 'object') {
        //         console.log('📦 Data is an object with keys:', Object.keys(event.detail.data));
        //     }

        //     // Only process if it's the assigned orders topic
        //     if (event.detail.topic === 'admin-assigned-orders') {
        //         console.log('✅ Topic matched: admin-assigned-orders - Processing...');
        //         const orderData = event.detail.data;

        //         // Handle both array and single object
        //         if (Array.isArray(orderData)) {
        //             console.log(`📦 Processing ${orderData.length} orders from WebSocket`);
        //             orderData.forEach((order, index) => {
        //                 console.log(`  📦 Order ${index + 1}:`, {
        //                     orderId: order.orderId || order.id,
        //                     status: order.status,
        //                     userName: order.userName,
        //                     createdAt: order.createdAt
        //                 });
        //             });
        //             orderData.forEach(order => handleNewOrder(order));
        //         } else if (orderData && typeof orderData === 'object') {
        //             console.log('📦 Processing single order from WebSocket');
        //             console.log('📋 Order details:', {
        //                 orderId: orderData.orderId || orderData.id,
        //                 status: orderData.status,
        //                 userName: orderData.userName,
        //                 createdAt: orderData.createdAt
        //             });
        //             handleNewOrder(orderData);
        //         } else {
        //             console.warn('⚠️ Unknown order data format:', orderData);
        //         }
        //     } else {
        //         console.log(`ℹ️ Ignoring message from topic: ${event.detail.topic}`);
        //         console.log(`ℹ️ Expected topic: admin-assigned-orders`);
        //     }

        //     console.log('═══════════════════════════════════════════════');
        // };

        const handleWebSocketMessage = (event) => {
            // console.log('═══════════════════════════════════════════════');
            // console.log('📨📨📨 WEBSOCKET MESSAGE RECEIVED! 📨📨📨');
            // console.log('═══════════════════════════════════════════════');
            // console.log('⏰ Received at:', new Date().toISOString());

            setWsMessageCount(prev => {
                const newCount = prev + 1;
                // console.log(`📊 WebSocket message count: ${newCount}`);
                return newCount;
            });

            // console.log('📋 Topic:', event.detail.topic);
            // console.log('📋 Topic Path:', event.detail.topicPath);
            // console.log('📦 Data type:', typeof event.detail.data);
            // console.log('📦 Raw Data:', event.detail.data);
            setRawWsData(event.detail.data);

            // if (Array.isArray(event.detail.data)) {
            //     console.log(`📦 Data is an array with ${event.detail.data.length} items`);
            //     console.log('📋 First item:', event.detail.data[0]);
            // } else if (typeof event.detail.data === 'object') {
            //     console.log('📦 Data is an object with keys:', Object.keys(event.detail.data));
            // }

            // Only process if it's the assigned orders topic
            if (event.detail.topic === 'admin-assigned-orders') {
                // console.log('✅ Topic matched: admin-assigned-orders - Processing...');
                const orderData = event.detail.data;

                // Handle both array and single object
                if (Array.isArray(orderData)) {
                    // console.log(`📦 Processing ${orderData.length} orders from WebSocket`);

                    // ✅ FILTER for FOOD_AND_BEVERAGES only
                    const filteredOrders = orderData.filter(order => {
                        const appType = order.appType?.toUpperCase() || '';
                        return appType === 'FOOD_AND_BEVERAGES';
                    });

                    // console.log(`✅ Filtered to ${filteredOrders.length} FOOD_AND_BEVERAGES orders`);

                    // filteredOrders.forEach((order, index) => {
                    //     console.log(`  📦 Order ${index + 1}:`, {
                    //         orderId: order.orderId || order.id,
                    //         status: order.status,
                    //         userName: order.userName,
                    //         createdAt: order.createdAt,
                    //         appType: order.appType
                    //     });
                    // });

                    filteredOrders.forEach(order => handleNewOrder(order));

                } else if (orderData && typeof orderData === 'object') {
                    // console.log('📦 Processing single order from WebSocket');
                    // console.log('📋 Order details:', {
                    //     orderId: orderData.orderId || orderData.id,
                    //     status: orderData.status,
                    //     userName: orderData.userName,
                    //     createdAt: orderData.createdAt,
                    //     appType: orderData.appType
                    // });

                    // ✅ Check single order for FOOD_AND_BEVERAGES
                    const appType = orderData.appType?.toUpperCase() || '';
                    if (appType === 'FOOD_AND_BEVERAGES') {
                        // console.log('✅ Single order is FOOD_AND_BEVERAGES, processing...');
                        handleNewOrder(orderData);
                    } else {
                        // console.log(`⏭️ Skipping non-FOOD_AND_BEVERAGES order: ${appType}`);
                    }
                } else {
                    // console.warn('⚠️ Unknown order data format:', orderData);
                }
            } else {
                // console.log(`ℹ️ Ignoring message from topic: ${event.detail.topic}`);
                // console.log(`ℹ️ Expected topic: admin-assigned-orders`);
            }

            // console.log('═══════════════════════════════════════════════');
        };

        // Add event listener for WebSocket messages
        // console.log('📡 Adding event listener: admin-websocket-message');
        window.addEventListener('admin-websocket-message', handleWebSocketMessage);

        // ─── Test WebSocket Connection ────────────────────────────
        setTimeout(() => {
            // console.log('🧪 Testing WebSocket connection after 3 seconds...');
            const isConnected = adminWebSocketService.isConnected();
            // console.log(`🧪 Connection test result: ${isConnected ? '✅ Connected' : '❌ Not connected'}`);

            if (isConnected) {
                const topics = adminWebSocketService.getTopics();
                // console.log('🧪 Active subscriptions:', topics);
            } else {
                // console.log('🧪 WebSocket not connected. Checking admin ID...');
                const adminId = localStorage.getItem('adminId') || localStorage.getItem('userId');
                // console.log('🧪 Admin ID found:', adminId);
                if (adminId) {
                    // console.log('🧪 Attempting to reconnect WebSocket...');
                    adminWebSocketService.connect(adminId);
                }
            }
        }, 3000);

        // ─── Cleanup ──────────────────────────────────────────────
        return () => {
            // console.log('🧹🧹🧹 Cleaning up DeliveryLiveOrders 🧹🧹🧹');
            // console.log('Removing WebSocket listener...');
            window.removeEventListener('admin-websocket-message', handleWebSocketMessage);
            // console.log('✅ Cleanup complete');
        };
    }, []);

    // ─── Manual Refresh (Only when user clicks) ──────────────────
    const handleRefresh = () => {
        // console.log('🔄🔄🔄 MANUAL REFRESH TRIGGERED BY USER 🔄🔄🔄');
        setRefreshing(true);
        fetchInitialOrders();
    };

    // ─── Clear New Order Notification ────────────────────────────
    const handleClearNotification = () => {
        // console.log('🔔 Clearing new order notifications');
        setNewOrderCount(0);
        setHasNewOrders(false);
        setNewOrderIds(new Set());
    };

    // ─── Helper Functions ─────────────────────────────────────────
    const getStatusColor = (status) => {
        if (!status) return '#e2e8f0';
        const s = status.toUpperCase();
        if (s === 'DELIVERED' || s === 'COMPLETED') return '#48bb78';
        if (s === 'CANCELLED') return '#fc8181';
        if (s === 'OFFERED' || s === 'PENDING') return '#f6e05e';
        if (s === 'ACCEPTED' || s === 'PICKED_UP') return '#4299e1';
        return '#e2e8f0';
    };

    // ✅ Format date and time
    const formatDateTimeIST = (dateString) => {
        if (!dateString) return '-';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            const day = String(date.getDate()).padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();

            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;

            return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
        } catch (error) {
            console.error('Date formatting error:', error, dateString);
            return dateString;
        }
    };

    const isNewOrder = (orderId) => {
        const isNew = newOrderIds.has(orderId);

        return isNew;
    };

    // ─── Render ───────────────────────────────────────────────────
    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <h2 style={styles.title}>
                    Live Delivery Orders
                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 'normal' }}>
                        ({orders.length} active)
                    </span>

                </h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {hasNewOrders && (
                        <button
                            onClick={handleClearNotification}
                            style={{
                                padding: '6px 12px',
                                background: 'transparent',
                                color: '#64748b',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                            }}
                        >
                            Clear Alert
                        </button>
                    )}
                    <button
                        onClick={handleRefresh}
                        style={styles.refreshButton}
                        disabled={loading || refreshing}
                    >
                        {refreshing ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSync />}
                        {refreshing ? 'Refreshing...' : 'Refresh Orders'}
                    </button>
                </div>
            </div>

            {/* Table Area */}
            <div style={styles.tableContainer}>
                {loading ? (
                    <div style={styles.loadingOverlay}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={28} color="#e66d33" />
                        <p style={{ marginTop: '12px', fontWeight: '500' }}>Loading live orders...</p>
                    </div>
                ) : error ? (
                    <div style={styles.emptyState}>
                        <p style={{ color: '#dc2626', fontWeight: '600' }}>⚠️ {error}</p>
                        <button
                            onClick={handleRefresh}
                            style={{ ...styles.refreshButton, margin: '12px auto' }}
                        >
                            Try Again
                        </button>
                    </div>
                ) : orders.length === 0 ? (
                    <div style={styles.emptyState}>
                        <h3 style={{ color: '#475569', margin: '0 0 8px 0' }}>No Live Orders</h3>
                        <p style={{ color: '#94a3b8', margin: '0' }}>
                            There are currently no active orders assigned to riders.
                            <br />New orders will appear here automatically.
                        </p>

                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Order ID</th>
                                <th style={styles.th}>Date & Time</th>
                                <th style={styles.th}>Customer</th>
                                <th style={styles.th}>Restaurant</th>
                                <th style={styles.th}>Items</th>
                                <th style={styles.th}>Partner ID</th>
                                <th style={styles.th}>Partner</th>

                                <th style={styles.th}>Earnings</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => {
                                const orderId = order.id || order.orderId;
                                const isNew = isNewOrder(orderId);

                                return (
                                    <tr
                                        key={orderId}
                                        style={{
                                            ...(isNew ? styles.newOrderRow : {}),
                                            background: isNew ? '#fef3e8' : 'transparent',
                                            transition: 'background 0.3s ease'
                                        }}
                                    >
                                        <td style={styles.td}>
                                            <strong>#{order.orderId}</strong>
                                            {isNew && (
                                                <span style={{
                                                    marginLeft: '8px',
                                                    padding: '2px 8px',
                                                    background: '#dc2626',
                                                    color: 'white',
                                                    borderRadius: '12px',
                                                    fontSize: '10px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    NEW
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            {formatDateTimeIST(order.createdAt)}
                                        </td>
                                        <td style={styles.td}>
                                            <div>{order.userName || 'Unknown'}</div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                {order.userPhone || '-'}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '500' }}>
                                                {order.vendorRestaurentname || 'Unknown'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                {order.deliveryDistanceKm ? `${parseFloat(order.deliveryDistanceKm).toFixed(2)} km away` : '-'}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {order.items || '-'}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <strong>#{order.partnerId}</strong>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '500' }}>
                                                {order.partnerName || 'Unknown'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                {order.partnerPhoneNumber || '-'}
                                            </div>
                                        </td>

                                        <td style={styles.td}>
                                            <span style={{ fontWeight: '600', color: '#16a34a' }}>
                                                ₹{order.earning ? order.earning.toFixed(2) : '0.00'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: getStatusColor(order.status),
                                                color: order.status?.toUpperCase() === 'OFFERED' ? '#92400e' : '#fff'
                                            }}>
                                                {order.status || 'PENDING'}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                style={styles.viewButton}
                                                onClick={() => navigate(`/admin/orders/details/${order.orderId}`)}
                                            >
                                                <FaEye /> View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Debug Info Panel */}
            {/* <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontSize: '12px',
                color: '#64748b',
                display: 'flex',
                gap: '20px',
                flexWrap: 'wrap'
            }}>
                <span>📊 Total orders displayed: {orders.length}</span>
                <span>🆕 New orders: {newOrderCount}</span>
                <span>📨 WS messages: {wsMessageCount}</span>
                <span>🔗 WS status: {adminWebSocketService.isConnected() ? '✅ Connected' : '❌ Disconnected'}</span>
                <span>📡 Initial load: {isInitialLoadComplete ? '✅' : '⏳'}</span>
                <span>📌 Topic: admin-assigned-orders</span>
                {rawApiData && <span>📦 API data count: {rawApiData.length}</span>}
                {rawWsData && <span>📦 Last WS data: {Array.isArray(rawWsData) ? `${rawWsData.length} items` : 'single item'}</span>}
            </div> */}

            {/* Inline Keyframes */}
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.5; }
                    100% { opacity: 1; }
                }
                @keyframes highlightNew {
                    0% { background: #fef3e8; }
                    50% { background: #ffe4d6; }
                    100% { background: #fef3e8; }
                }
            `}</style>
        </div>
    );
};

export default DeliveryLiveOrders;